import { useState } from "react";
import { useParams, Link } from "react-router-dom";
// ⚠️ Best-guess hook names — verify against generated v1.ts (Rule MKT-FE-05).
import {
  useV1MarketplaceListingsRetrieve,
  useV1MarketplaceListingPricesList,
  useV1MarketplaceListingPricesCreate,
  useV1MarketplaceListingsDeactivateCreate,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function ListingDetailPage() {
  const { id = "" } = useParams();
  const { data: listingRes, refetch: refetchListing } =
    useV1MarketplaceListingsRetrieve(id);
  const { data: listingPrices, refetch: refetchPrices } = useV1MarketplaceListingPricesList({ listing: id } as any);
  const createPrice = useV1MarketplaceListingPricesCreate();
  const deactivateMutation = useV1MarketplaceListingsDeactivateCreate();

  const [basePrice, setBasePrice] = useState("");
  const [currency, setCurrency] = useState("USD");

  const listing = listingRes;
  const prices = listingPrices?.results ?? [];

  const handleNewPrice = (e: React.FormEvent) => {
    e.preventDefault();
    createPrice.mutate(
      { data: { listing: id, base_price: basePrice, currency } as any },
      { onSuccess: () => { refetchPrices(); setBasePrice(""); } }
    );
  };

  if (!listing) return <div style={{ padding: "2rem" }}>در حال بارگذاری...</div>;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`آگهی ${listing.id}`}
        subtitle={`وضعیت: ${listing.is_active ? "فعال" : "غیرفعال"}`}
        icon="🏷️"
        helpLink="/help/marketplace/listing-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {listing.is_active && (
            <button
              onClick={() =>
                deactivateMutation.mutate({ id: id, data: {} } as any, { onSuccess: () => refetchListing() })
              }
              disabled={deactivateMutation.isPending}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ff4d4d",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: deactivateMutation.isPending ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(255, 77, 77, 0.2)",
                opacity: deactivateMutation.isPending ? 0.7 : 1,
              }}
            >
              {deactivateMutation.isPending ? "در حال انجام..." : "غیرفعال‌سازی آگهی"}
            </button>
          )}
          <Link
            to="/marketplace/listings"
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            ← بازگشت
          </Link>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        <div>
          <h2 style={{ fontSize: "1.1rem", fontWeight: "bold", marginBottom: "1rem" }}>تاریخچهٔ قیمت</h2>
          <div className="table-container" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>قیمت پایه</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>ارز</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>از</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>تا</th>
                </tr>
              </thead>
              <tbody>
                {prices?.map((p: any) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "1rem", fontWeight: "bold" }}>{p.base_price}</td>
                    <td style={{ padding: "1rem" }}>{p.currency}</td>
                    <td style={{ padding: "1rem", direction: "ltr" }}>{p.valid_from ? new Date(p.valid_from).toLocaleString("fa-IR") : "—"}</td>
                    <td style={{ padding: "1rem", direction: "ltr" }}>{p.valid_until ? new Date(p.valid_until).toLocaleString("fa-IR") : "فعال"}</td>
                  </tr>
                ))}
                {prices.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ قیمتی یافت نشد.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ padding: "1rem", background: "rgba(255,255,255,0.02)", borderRadius: "12px", border: "1px solid var(--glass-border)" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>ثبت قیمت جدید</h3>
          <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            ثبت قیمت جدید، قیمت قبلی را به صورت خودکار می‌بندد.
          </p>
          <form onSubmit={handleNewPrice} style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1", minWidth: "150px" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>قیمت پایه</label>
              <input
                className="input-field"
                placeholder="مثلا 1000"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                required
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ flex: "1", minWidth: "150px" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>ارز</label>
              <input
                className="input-field"
                placeholder="USD"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                required
                style={{ width: "100%" }}
              />
            </div>
            <button type="submit" disabled={createPrice.isPending} className="btn-primary" style={{ padding: "0.75rem 1.5rem" }}>
              {createPrice.isPending ? "در حال ثبت..." : "ثبت قیمت"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
